import React, { useMemo, useState } from 'react';
import {
  resolveAdvancedYearProfile,
  resolveAdvancedCell,
  impliedYoYPercent,
  MAX_RETURN_HORIZON_YEARS,
} from '../../utils/returnHypothesisMath';

function formatMoney(value, locale = 'fr-CA') {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPct(value, locale = 'fr-CA') {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${Number(value).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`;
}

function ModeToggle({ activeMode, onChange, disabled }) {
  const modes = [
    { id: 'percent', label: '%' },
    { id: 'amount', label: '$' },
  ];
  return (
    <div className="flex rounded border border-gray-200 overflow-hidden text-xs shrink-0">
      {modes.map((m) => (
        <button
          key={m.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(m.id)}
          className={`px-1.5 py-0.5 min-w-[1.75rem] ${
            activeMode === m.id
              ? 'bg-green-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50'
          } disabled:opacity-50`}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function ScheduleCell({
  cell,
  onChange,
  previousResolved,
  disabled,
  locale,
  language,
}) {
  const mode = cell?.mode || 'percent';
  const inputValue =
    mode === 'percent' ? (cell?.percent ?? '') : (cell?.amount ?? '');

  const resolved = resolveAdvancedCell(cell, previousResolved);
  const hint =
    mode === 'percent'
      ? formatMoney(resolved, locale)
      : formatPct(impliedYoYPercent(previousResolved, resolved), locale);

  const en = language === 'en';

  return (
    <div className="min-w-[7rem]">
      <div className="flex gap-1 items-stretch">
        <ModeToggle
          activeMode={mode}
          onChange={(nextMode) => {
            if (nextMode === 'percent') {
              onChange({ mode: 'percent', percent: cell?.percent ?? 0 });
            } else {
              onChange({ mode: 'amount', amount: cell?.amount ?? Math.round(resolved) });
            }
          }}
          disabled={disabled}
        />
        <input
          type="number"
          step={mode === 'amount' ? 1000 : 0.1}
          value={inputValue}
          disabled={disabled}
          onChange={(e) => {
            const parsed = parseFloat(e.target.value);
            const val = Number.isFinite(parsed) ? parsed : 0;
            if (mode === 'percent') {
              onChange({ mode: 'percent', percent: val });
            } else {
              onChange({ mode: 'amount', amount: val });
            }
          }}
          className="flex-1 min-w-0 w-16 px-1 py-0.5 border rounded text-sm disabled:bg-gray-50"
        />
      </div>
      <p className="text-[10px] text-gray-500 mt-0.5">
        {en ? '→' : '→'} {hint}
      </p>
    </div>
  );
}

function ColumnApplyHeader({ columnKey, onApply, disabled, t }) {
  const [globalCell, setGlobalCell] = useState({ mode: 'percent', percent: 0 });

  const apply = () => {
    onApply(columnKey, globalCell.mode === 'percent'
      ? { mode: 'percent', percent: globalCell.percent ?? 0 }
      : { mode: 'amount', amount: globalCell.amount ?? 0 });
  };

  const mode = globalCell.mode || 'percent';
  const inputVal = mode === 'percent' ? globalCell.percent ?? '' : globalCell.amount ?? '';

  return (
    <div className="flex flex-col gap-1 mb-2">
      <div className="flex gap-1 items-stretch">
        <ModeToggle
          activeMode={mode}
          onChange={(m) =>
            setGlobalCell(
              m === 'percent'
                ? { mode: 'percent', percent: 0 }
                : { mode: 'amount', amount: 0 },
            )
          }
          disabled={disabled}
        />
        <input
          type="number"
          step={mode === 'amount' ? 1000 : 0.1}
          value={inputVal}
          disabled={disabled}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            const val = Number.isFinite(v) ? v : 0;
            setGlobalCell(
              mode === 'percent'
                ? { mode: 'percent', percent: val }
                : { mode: 'amount', amount: val },
            );
          }}
          className="flex-1 min-w-0 px-1 py-0.5 border rounded text-xs"
        />
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={apply}
        className="text-[10px] text-green-700 hover:text-green-900 underline disabled:opacity-50 text-left"
      >
        {t('propertyReport.futureReturns.advanced.applyColumn')}
      </button>
    </div>
  );
}

export default function AdvancedReturnsModal({
  open,
  onClose,
  onSave,
  schedule,
  onScheduleChange,
  bases,
  onPrefillFromSimple,
  onReset,
  disabled,
  t,
  language,
}) {
  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  const en = language === 'en';

  const profile = useMemo(
    () => resolveAdvancedYearProfile(bases, schedule),
    [bases, schedule],
  );

  const baseline = useMemo(
    () => ({
      revenue: Number(bases.baseRevenue) || 0,
      expense: Number(bases.baseExpense) || 0,
      value: Number(bases.purchasePrice) || 0,
      netIncome:
        (Number(bases.baseRevenue) || 0) - (Number(bases.baseExpense) || 0),
    }),
    [bases],
  );

  const warnings = profile.resolvedRows.filter((r) => r.netIncome < 0);

  const updateRow = (yearIndex, field, cell) => {
    const rows = [...(schedule.rows || [])];
    while (rows.length < schedule.years) {
      rows.push({ revenue: null, expense: null, value: null });
    }
    rows[yearIndex] = { ...rows[yearIndex], [field]: cell };
    onScheduleChange({ ...schedule, rows });
  };

  const applyColumn = (field, cell) => {
    const rows = (schedule.rows || []).map((row) => ({
      ...row,
      [field]: { ...cell },
    }));
    onScheduleChange({ ...schedule, rows });
  };

  const clearCell = (yearIndex, field) => {
    updateRow(yearIndex, field, null);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="advanced-returns-title"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 shrink-0">
          <h3 id="advanced-returns-title" className="text-lg font-semibold text-gray-900">
            {t('propertyReport.futureReturns.advanced.modalTitle')}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {t('propertyReport.futureReturns.advanced.modalDesc')}
          </p>
        </div>

        <div className="p-4 overflow-auto flex-1">
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              type="button"
              disabled={disabled}
              onClick={onPrefillFromSimple}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {t('propertyReport.futureReturns.advanced.prefillSimple')}
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={onReset}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {t('propertyReport.futureReturns.advanced.reset')}
            </button>
          </div>

          {warnings.length > 0 ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
              {t('propertyReport.futureReturns.advanced.negativeNetWarning')}
            </p>
          ) : null}

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-3 font-medium text-gray-700 w-20">
                    {t('propertyReport.futureReturns.advanced.yearCol')}
                  </th>
                  <th className="text-left py-2 px-2 font-medium text-gray-700">
                    {t('propertyReport.futureReturns.revenue')}
                    <ColumnApplyHeader
                      columnKey="revenue"
                      onApply={applyColumn}
                      disabled={disabled}
                      t={t}
                    />
                  </th>
                  <th className="text-left py-2 px-2 font-medium text-gray-700">
                    {t('propertyReport.futureReturns.expense')}
                    <ColumnApplyHeader
                      columnKey="expense"
                      onApply={applyColumn}
                      disabled={disabled}
                      t={t}
                    />
                  </th>
                  <th className="text-left py-2 px-2 font-medium text-gray-700">
                    {t('propertyReport.futureReturns.propertyValue')}
                    <ColumnApplyHeader
                      columnKey="value"
                      onApply={applyColumn}
                      disabled={disabled}
                      t={t}
                    />
                  </th>
                  <th className="text-right py-2 pl-2 font-medium text-gray-700">
                    {t('propertyReport.futureReturns.netIncomeCaption')}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <td className="py-2 pr-3 text-gray-600 font-medium">
                    {t('propertyReport.futureReturns.advanced.year0')}
                  </td>
                  <td className="py-2 px-2 text-gray-700">
                    {formatMoney(baseline.revenue, locale)}
                  </td>
                  <td className="py-2 px-2 text-gray-700">
                    {formatMoney(baseline.expense, locale)}
                  </td>
                  <td className="py-2 px-2 text-gray-700">
                    {formatMoney(baseline.value, locale)}
                  </td>
                  <td className="py-2 pl-2 text-right text-gray-700">
                    {formatMoney(baseline.netIncome, locale)}
                  </td>
                </tr>
                {Array.from({ length: schedule.years }, (_, i) => {
                  const row = schedule.rows[i] || {};
                  const prevRev =
                    i === 0
                      ? baseline.revenue
                      : profile.resolvedRows[i - 1]?.revenue ?? baseline.revenue;
                  const prevExp =
                    i === 0
                      ? baseline.expense
                      : profile.resolvedRows[i - 1]?.expense ?? baseline.expense;
                  const prevVal =
                    i === 0
                      ? baseline.value
                      : profile.resolvedRows[i - 1]?.value ?? baseline.value;
                  const resolved = profile.resolvedRows[i];

                  return (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 pr-3 text-gray-700 font-medium">
                        {i + 1}
                      </td>
                      <td className="py-2 px-2 align-top">
                        <ScheduleCell
                          cell={row.revenue}
                          previousResolved={prevRev}
                          onChange={(c) => updateRow(i, 'revenue', c)}
                          disabled={disabled}
                          locale={locale}
                          language={language}
                        />
                        {row.revenue != null ? (
                          <button
                            type="button"
                            className="text-[10px] text-gray-400 hover:text-gray-600 mt-0.5"
                            onClick={() => clearCell(i, 'revenue')}
                          >
                            {t('propertyReport.futureReturns.advanced.clearCell')}
                          </button>
                        ) : (
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {en ? 'Inherited' : 'Hérité'}
                          </p>
                        )}
                      </td>
                      <td className="py-2 px-2 align-top">
                        <ScheduleCell
                          cell={row.expense}
                          previousResolved={prevExp}
                          onChange={(c) => updateRow(i, 'expense', c)}
                          disabled={disabled}
                          locale={locale}
                          language={language}
                        />
                        {row.expense != null ? (
                          <button
                            type="button"
                            className="text-[10px] text-gray-400 hover:text-gray-600 mt-0.5"
                            onClick={() => clearCell(i, 'expense')}
                          >
                            {t('propertyReport.futureReturns.advanced.clearCell')}
                          </button>
                        ) : (
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {en ? 'Inherited' : 'Hérité'}
                          </p>
                        )}
                      </td>
                      <td className="py-2 px-2 align-top">
                        <ScheduleCell
                          cell={row.value}
                          previousResolved={prevVal}
                          onChange={(c) => updateRow(i, 'value', c)}
                          disabled={disabled}
                          locale={locale}
                          language={language}
                        />
                        {row.value != null ? (
                          <button
                            type="button"
                            className="text-[10px] text-gray-400 hover:text-gray-600 mt-0.5"
                            onClick={() => clearCell(i, 'value')}
                          >
                            {t('propertyReport.futureReturns.advanced.clearCell')}
                          </button>
                        ) : (
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {en ? 'Inherited' : 'Hérité'}
                          </p>
                        )}
                      </td>
                      <td className="py-2 pl-2 text-right align-top text-gray-800 font-medium">
                        {formatMoney(resolved?.netIncome, locale)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              onSave(schedule);
              onClose();
            }}
            disabled={disabled}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {t('propertyReport.futureReturns.advanced.saveGrid')}
          </button>
        </div>
      </div>
    </div>
  );
}

export { MAX_RETURN_HORIZON_YEARS };
